import { useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../services/analytics';
import api from '../services/api';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Input,
  Textarea,
  SimpleGrid,
  Alert,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { 
  FaArrowLeft, 
  FaEnvelope, 
  FaFacebook,
  FaInstagram,
  FaTiktok,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import Footer from './Footer';
import SEO from './SEO.jsx';

const Contacto = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: searchParams.get('asunto') || '',
    mensaje: searchParams.get('mensaje') || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
 

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await api.post('/contacto', formData);

      if (response.data.success) {
        trackEvent('contact_form_submit', { subject: formData.asunto });
        toaster.create({
          title: 'Mensaje enviado',
          description: 'Gracias por contactarnos. Te responderemos pronto.',
          type: 'success',
          duration: 5000,
          closable: true,
        });
        setFormData({ nombre: '', email: '', asunto: '', mensaje: '' });
      } else {
        toaster.create({
          title: 'Error',
          description: response.data.message || 'No se pudo enviar el mensaje. Inténtalo de nuevo.',
          type: 'error',
          duration: 5000,
          closable: true,
          position: 'top',
        });
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: 'No se pudo enviar el mensaje. Inténtalo de nuevo más tarde.',
        type: 'error',
        duration: 5000,
        closable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      title: 'Email',
      info: 'soporte@cadencya.com',
      link: 'mailto:soporte@cadencya.com',
    },
  ];

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <SEO
        title="Contacto"
        description="Contacta con el equipo de Cadencya. Estamos aquí para ayudarte con cualquier duda sobre nuestra plataforma."
        path="/contacto"
      />
      {/* Header */}
      <Box bg="purple.600" color="white" py={6}>
        <Container maxW="7xl">
          <Button
            variant="ghost"
            color="white"
            _hover={{ bg: 'purple.700' }}
            mb={4}
            asChild><RouterLink to="/"><Icon asChild><FaArrowLeft /></Icon>Volver al inicio
                                    </RouterLink></Button>
          <Heading size="2xl" mb={4}>
            Contacto
          </Heading>
          <Text fontSize="xl" maxW="3xl">
            ¿Tienes alguna pregunta o sugerencia? Estamos aquí para ayudarte.
          </Text>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Box flex="1" bg={bgColor} py={16}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={12}>
            {/* Formulario de contacto */}
            <Box>
              <Heading size="lg" mb={6} color="purple.600">
                Envíanos un mensaje
              </Heading>
              <Box bg={cardBg} p={8} rounded="2xl" shadow="lg" asChild><form onSubmit={handleSubmit}>
                  <VStack gap={6}>
                    <Field.Root required>
                      <Field.Label>Nombre completo</Field.Label>
                      <Input
                        name="nombre"
                        value={formData.nombre}
                        onValueChange={handleChange}
                        placeholder="Tu nombre"
                        size="lg"
                        rounded="lg"
                      />
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>Email</Field.Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onValueChange={handleChange}
                        placeholder="tu@email.com"
                        size="lg"
                        rounded="lg"
                      />
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>Asunto</Field.Label>
                      <Input
                        name="asunto"
                        value={formData.asunto}
                        onValueChange={handleChange}
                        placeholder="¿En qué podemos ayudarte?"
                        size="lg"
                        rounded="lg"
                      />
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>Mensaje</Field.Label>
                      <Textarea
                        name="mensaje"
                        value={formData.mensaje}
                        onValueChange={handleChange}
                        placeholder="Escribe tu mensaje aquí..."
                        rows={6}
                        size="lg"
                        rounded="lg"
                      />
                    </Field.Root>

                    <Button
                      type="submit"
                      colorPalette="purple"
                      size="lg"
                      width="full"
                      rounded="lg"
                      loading={isSubmitting}
                      loadingText="Enviando..."
                    >
                      Enviar mensaje
                    </Button>
                  </VStack>
                </form></Box>
            </Box>

            {/* Información de contacto */}
            <Box>
              <Heading size="lg" mb={6} color="purple.600">
                Información de contacto
              </Heading>
              
              <VStack gap={6} align="stretch">
                {contactInfo.map((item, index) => (
                  <Box
                    key={index}
                    bg={cardBg}
                    p={6}
                    rounded="xl"
                    shadow="md"
                    transition="all 0.3s"
                    _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                    cursor={item.link ? 'pointer' : 'default'}
                    onClick={item.link ? () => window.location.href = item.link : undefined}
                  >
                    <HStack gap={4}>
                      <Box
                        bg="purple.100"
                        p={3}
                        rounded="full"
                        display="inline-flex"
                      >
                        <Icon w={6} h={6} color="purple.600" asChild><item.icon /></Icon>
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontWeight="600" color="gray.700">
                          {item.title}
                        </Text>
                        <Text color="gray.600">
                          {item.info}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}

                {/* Redes sociales */}
                <Box
                  bg={cardBg}
                  p={6}
                  rounded="xl"
                  shadow="md"
                >
                  <Text fontWeight="600" color="gray.700" mb={4}>
                    Síguenos en redes sociales
                  </Text>
                  <HStack gap={4}>
                    <Button colorPalette="facebook" variant="outline" size="sm" asChild><a
                        href="https://www.facebook.com/profile.php?id=61579336839907"
                        target="_blank"><Icon asChild><FaFacebook /></Icon>Facebook
                                                                  </a></Button>
                    <Button colorPalette="gray" variant="outline" size="sm" asChild><a href="https://x.com/cadencya_app" target="_blank"><Icon asChild><FaXTwitter /></Icon>X
                                                                  </a></Button>
                    <Button colorPalette="pink" variant="outline" size="sm" asChild><a href="https://www.instagram.com/cadencya_app/" target="_blank"><Icon asChild><FaInstagram /></Icon>Instagram
                                                                  </a></Button>
                    <Button colorPalette="gray" variant="outline" size="sm" asChild><a href="https://www.tiktok.com/@cadencya_app" target="_blank"><Icon asChild><FaTiktok /></Icon>TikTok
                                                                  </a></Button>
                  </HStack>
                </Box>
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Mensaje adicional */}
          <Box
            mt={16}
            bg={cardBg}
            p={8}
            rounded="2xl"
            shadow="lg"
            textAlign="center"
          >
            <Heading size="md" mb={4} color="purple.600">
              ¿Necesitas ayuda inmediata?
            </Heading>
            <Text color="gray.600" maxW="2xl" mx="auto">
              Si tienes un problema técnico urgente o necesitas asistencia con tu cuenta, 
              consulta nuestra sección de ayuda o escríbenos directamente a{' '}
              <Text as="span" color="purple.600" fontWeight="600">
                soporte@cadencya.com
              </Text>
            </Text>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default Contacto;
